import React, { useState, useEffect } from 'react';
import CountryFlag, { countryFromRegion } from '../../../shared/components/CountryFlag';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    message,
    Badge,
    Popconfirm,
    Tooltip,
    Progress,
    Drawer,
    Descriptions,
    Row,
    Col,
    Statistic,
    Empty,
    Spin,
    Switch
} from 'antd';
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import hostSharedService from '../../../shared/services/hostSharedService';
import hostSharedAdminService from '../../services/hostSharedAdminService';

const { Option } = Select;
const { TextArea } = Input;

const Nodes = () => {
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingNode, setEditingNode] = useState(null);
    const [statsDrawerVisible, setStatsDrawerVisible] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [nodeStats, setNodeStats] = useState(null);
    const [statsNode, setStatsNode] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        loadNodes();
    }, []);

    const loadNodes = async () => {
        setLoading(true);
        try {
            const response = await hostSharedService.getNodes();
            if (response.success) {
                // 后端返回格式: { success: true, data: { list: [...], total: ... } }
                const nodesList = response.data?.list || response.data || [];
                setNodes(Array.isArray(nodesList) ? nodesList : []);
            } else {
                message.error(response.error || '加载节点列表失败');
            }
        } catch (error) {
            message.error('加载节点列表失败: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingNode(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingNode(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            let response;
            if (editingNode) {
                response = await hostSharedService.updateNode(editingNode.id, values);
            } else {
                response = await hostSharedService.createNode(values);
            }

            if (response.success) {
                message.success(editingNode ? '节点更新成功' : '节点创建成功');
                setModalVisible(false);
                loadNodes();
            } else {
                message.error(response.error || '操作失败');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写');
            } else {
                message.error('操作失败: ' + error.message);
            }
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await hostSharedService.deleteNode(id);
            if (response.success) {
                message.success('节点已删除');
                loadNodes();
            } else {
                message.error(response.error || '删除失败');
            }
        } catch (error) {
            message.error('删除失败: ' + error.message);
        }
    };

    const handleViewStats = async (record) => {
        setStatsNode(record);
        setNodeStats(null);
        setStatsDrawerVisible(true);
        setStatsLoading(true);
        try {
            const response = await hostSharedAdminService.getNodeStats(record.id);
            if (response.success) {
                setNodeStats(response.data || null);
            } else {
                message.error(response.error || '加载节点统计失败');
            }
        } catch (error) {
            message.error('加载节点统计失败: ' + error.message);
        } finally {
            setStatsLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'online': { status: 'success', text: '在线' },
            'offline': { status: 'error', text: '离线' },
            'maintenance': { status: 'warning', text: '维护中' }
        };
        const config = statusMap[status] || { status: 'default', text: '未知' };
        return <Badge status={config.status} text={config.text} />;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 80,
        },
        {
            title: '节点名称',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'API URL',
            dataIndex: 'api_url',
            key: 'api_url',
            ellipsis: true,
        },
        {
            title: 'CDN API URL',
            dataIndex: 'cdn_api_url',
            key: 'cdn_api_url',
            ellipsis: true,
            render: (text) => text || <span style={{ color: '#999' }}>未配置</span>
        },
        {
            title: 'phpMyAdmin URL',
            dataIndex: 'phpmyadmin_url',
            key: 'phpmyadmin_url',
            ellipsis: true,
            render: (text) => text || <span style={{ color: '#999' }}>未配置</span>
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusBadge(status),
        },
        {
            title: '地区',
            dataIndex: 'region',
            key: 'region',
            render: (text) => (text ? <><CountryFlag code={countryFromRegion(text)} />{text}</> : '-'),
        },
        {
            title: '容量/已用',
            key: 'capacity',
            render: (_, record) => {
                const percent = record.capacity > 0
                    ? Math.round((record.current_count / record.capacity) * 100)
                    : 0;
                return (
                    <div>
                        <div>{record.current_count || 0} / {record.capacity || 0}</div>
                        <Progress percent={percent} size="small" status={percent >= 90 ? 'exception' : 'normal'} />
                    </div>
                );
            },
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
        },
        {
            title: '最后检查',
            dataIndex: 'last_check',
            key: 'last_check',
            render: (text) => text || '-',
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 240,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<BarChartOutlined />}
                        onClick={() => handleViewStats(record)}
                    >
                        统计
                    </Button>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Popconfirm
                        title="确定要删除这个节点吗？"
                        description="删除前请确保该节点上没有虚拟主机"
                        onConfirm={() => handleDelete(record.id)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={(record.current_count || 0) > 0}
                        >
                            删除
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Card
                title="共享主机节点管理"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={loadNodes}>
                            刷新
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            添加节点
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={nodes}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1400 }}
                />
            </Card>

            <Modal
                title={editingNode ? '编辑节点' : '添加节点'}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => setModalVisible(false)}
                width={700}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="节点名称"
                        name="name"
                        rules={[{ required: true, message: '请输入节点名称' }]}
                    >
                        <Input placeholder="node1" />
                    </Form.Item>

                    <Form.Item
                        label="API URL"
                        name="api_url"
                        rules={[{ required: true, message: '请输入 API URL' }]}
                        extra="EasyPanel 节点的 API 地址，例如: http://192.168.1.100/host_shared/api/index.php"
                    >
                        <Input placeholder="http://node1.example.com/host_shared/api/index.php" />
                    </Form.Item>

                    <Form.Item
                        label="CDN API URL"
                        name="cdn_api_url"
                        extra="可选。通过 Cloudflare CDN 访问的 API 地址，用于文件管理操作（提供 SSL 和加速）"
                    >
                        <Input placeholder="https://node1.cdn.example.com/host_shared/api/index.php" />
                    </Form.Item>

                    <Form.Item
                        label="phpMyAdmin URL"
                        name="phpmyadmin_url"
                        extra="可选。phpMyAdmin 访问地址，供用户管理数据库使用"
                    >
                        <Input placeholder="https://phpmyadmin.example.com" />
                    </Form.Item>

                    <Form.Item
                        label="节点密钥 (skey)"
                        name="skey"
                        rules={[{ required: true, message: '请输入节点密钥' }]}
                        extra="EasyPanel 节点配置的密钥，用于 JWT 认证"
                    >
                        <Input.Password placeholder="your-secure-secret-key" />
                    </Form.Item>

                    <Form.Item
                        label="容量"
                        name="capacity"
                        rules={[{ required: true, message: '请输入容量' }]}
                    >
                        <Input type="number" placeholder="100" />
                    </Form.Item>

                    <Form.Item
                        label="优先级"
                        name="priority"
                        initialValue={100}
                        extra="优先级越高，越优先分配虚拟主机到该节点"
                    >
                        <Input type="number" placeholder="100" />
                    </Form.Item>

                    <Form.Item
                        label="地区"
                        name="region"
                    >
                        <Input placeholder="cn, us, eu 等" />
                    </Form.Item>

                    <Form.Item
                        label="状态"
                        name="status"
                        initialValue="online"
                    >
                        <Select placeholder="选择状态">
                            <Option value="online">在线</Option>
                            <Option value="offline">离线</Option>
                            <Option value="maintenance">维护中</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item label="测试节点" name="is_test" valuePropName="checked" tooltip="不参与自动分配">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 节点实时统计 */}
            <Drawer
                title={`节点统计 - ${statsNode?.name || ''}`}
                open={statsDrawerVisible}
                onClose={() => setStatsDrawerVisible(false)}
                width={720}
            >
                {statsLoading && (
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin />
                    </div>
                )}
                {!statsLoading && !nodeStats && <Empty description="暂无统计数据" />}
                {!statsLoading && nodeStats && (
                    <div>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={8}>
                                <Card size="small">
                                    <Statistic title="虚拟主机总数" value={nodeStats.vhost_stats?.total || 0} />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Card size="small">
                                    <Statistic
                                        title="运行中"
                                        value={nodeStats.vhost_stats?.active || 0}
                                        valueStyle={{ color: '#3f8600' }}
                                    />
                                </Card>
                            </Col>
                            <Col xs={12} sm={8}>
                                <Card size="small">
                                    <Statistic
                                        title="已暂停"
                                        value={nodeStats.vhost_stats?.suspended || 0}
                                        valueStyle={{ color: '#faad14' }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Card size="small" title="容量" style={{ marginTop: 16 }}>
                            <Descriptions column={2} size="small">
                                <Descriptions.Item label="总容量">{nodeStats.node?.capacity || 0}</Descriptions.Item>
                                <Descriptions.Item label="已用">{nodeStats.node?.current_count || 0}</Descriptions.Item>
                                <Descriptions.Item label="剩余">{nodeStats.node?.remaining_capacity || 0}</Descriptions.Item>
                                <Descriptions.Item label="使用率">{nodeStats.node?.usage_percent || 0}%</Descriptions.Item>
                            </Descriptions>
                            <Progress
                                percent={Math.round(nodeStats.node?.usage_percent || 0)}
                                status={(nodeStats.node?.usage_percent || 0) >= 90 ? 'exception' : 'active'}
                            />
                        </Card>

                        <Card size="small" title="最近创建的虚拟主机" style={{ marginTop: 16 }}>
                            <Table
                                size="small"
                                rowKey="id"
                                pagination={false}
                                dataSource={nodeStats.recent_vhosts || []}
                                locale={{ emptyText: <Empty description="暂无数据" /> }}
                                columns={[
                                    { title: 'ID', dataIndex: 'id', width: 70 },
                                    { title: '名称', dataIndex: 'name' },
                                    { title: '用户ID', dataIndex: 'user_id' },
                                    {
                                        title: '状态',
                                        dataIndex: 'status',
                                        render: (s) => (s === 1 ? <Tag color="green">运行中</Tag> : <Tag>已暂停</Tag>),
                                    },
                                    { title: '创建时间', dataIndex: 'created_at' },
                                ]}
                            />
                        </Card>

                        <Card size="small" title="近 7 天使用率趋势" style={{ marginTop: 16 }}>
                            <Table
                                size="small"
                                rowKey="date"
                                pagination={false}
                                dataSource={nodeStats.usage_trend || []}
                                locale={{ emptyText: <Empty description="暂无数据" /> }}
                                columns={[
                                    { title: '日期', dataIndex: 'date' },
                                    { title: '虚拟主机数', dataIndex: 'count' },
                                    {
                                        title: '使用率',
                                        dataIndex: 'percent',
                                        render: (p) => `${p || 0}%`,
                                    },
                                ]}
                            />
                        </Card>
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default Nodes;
